import { Injectable, inject, signal } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';

export type FeatureName =
  | 'publications'
  | 'congresses'
  | 'ctmsMilestones'
  | 'customMilestones'
  | 'customActivities'
  | 'vvpmActivities'
  | 'strategicPillars'
  | 'veeva'
  | 'audiences';

export interface DeploymentConfig {
  deploymentType: 'pharma' | 'diagnostics';
  features: Record<FeatureName, boolean>;
}

// Used until /api/config answers, and kept if it never does. Matches the full
// Pharma application so an unreachable config endpoint cannot silently strip
// features from the deployment that has them.
const DEFAULT_CONFIG: DeploymentConfig = {
  deploymentType: 'pharma',
  features: {
    publications: true,
    congresses: true,
    ctmsMilestones: true,
    customMilestones: true,
    customActivities: true,
    vvpmActivities: true,
    strategicPillars: true,
    veeva: true,
    audiences: true,
  },
};

/**
 * Feature set of the backend this frontend is talking to.
 *
 * The backend owns the flags and publishes them at /api/config, so a single
 * frontend build serves both the Pharma and Diagnostics deployments and the two
 * sides cannot drift out of sync.
 */
@Injectable({ providedIn: 'root' })
export class FeatureService {
  private readonly http = inject(HttpClient);
  private readonly config = signal<DeploymentConfig>(DEFAULT_CONFIG);

  readonly deploymentType = () => this.config().deploymentType;
  readonly isDiagnostics = () => this.config().deploymentType === 'diagnostics';

  /** Resolved during bootstrap, before the first route renders. */
  async load(): Promise<void> {
    try {
      const config = await firstValueFrom(this.http.get<DeploymentConfig>('/api/config'));
      if (config?.features) {
        this.config.set({
          deploymentType: config.deploymentType ?? DEFAULT_CONFIG.deploymentType,
          features: { ...DEFAULT_CONFIG.features, ...config.features },
        });
      }
    } catch {
      console.warn('Could not load /api/config; assuming the full feature set.');
    }
  }

  isEnabled(feature: FeatureName): boolean {
    return this.config().features[feature] ?? false;
  }

  isDisabled(feature: FeatureName): boolean {
    return !this.isEnabled(feature);
  }

  /**
   * Whether a timeline entity type has any backing data source.
   *
   * Takes a plain string rather than EntityTypeFilter because models/filter.ts
   * imports the services that import this one.
   */
  isEntityTypeEnabled(entityType: string): boolean {
    switch (entityType) {
      case 'activities':
        return this.isEnabled('customActivities');
      case 'vvpmActivities':
        return this.isEnabled('vvpmActivities');
      case 'milestones':
        return this.isEnabled('ctmsMilestones') || this.isEnabled('customMilestones');
      case 'publications':
        return this.isEnabled('publications');
      case 'congresses':
        return this.isEnabled('congresses');
      default:
        return true;
    }
  }
}
